import { useCallback, useEffect, useMemo } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { useNetworksStateContext } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import { Pagination, useIsMobileDevice } from "@l2r-front/l2r-ui"
import { useApplicationStateContext, useUrlParams } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../constants/i18n"
import { DOCUMENTS_COUNT_PER_PAGE } from "../../constants/pages"
import { ROAD_DOCUMENTS_PAGE } from "../../constants/urlParams"
import { useDocumentStateContext } from "../../contexts/DocumentContext"
import { useDocuments } from "../../hooks/queries/useDocument"

import * as Styled from "./DocumentRoadSidebarContent.styled"

export function DocumentRoadSidebarContent(props) {

    const {
        className,
    } = props

    const { road } = useParams()
    const { getParam, setParam } = useUrlParams()
    const { currentBottomSheetHeight } = useApplicationStateContext()
    const isMobile = useIsMobileDevice()
    const { t } = useTranslation(I18N_NAMESPACE)
    const { apiFilters } = useDocumentStateContext()
    const { selectedSubNetworkSlugs } = useNetworksStateContext()

    const page = useMemo(() => {
        return parseInt(getParam(ROAD_DOCUMENTS_PAGE)) || 1
    }, [getParam])

    const { data, isLoading, isError } = useDocuments(
        {
            offset: (page - 1) * DOCUMENTS_COUNT_PER_PAGE,
            limit: DOCUMENTS_COUNT_PER_PAGE,
            ...(road && { linear_location_road: road }),
            ...(apiFilters && { ...apiFilters }),
        })

    const pageCount = useMemo(() => {
        if (!data || !data.count) {
            return 0
        }
        return Math.ceil(data.count / DOCUMENTS_COUNT_PER_PAGE)
    }, [data])

    const handleChangePage = useCallback(
        (_, newPage) => {
            setParam(ROAD_DOCUMENTS_PAGE, newPage)
        }, [setParam],
    )

    useEffect(() => {
        if (!isLoading && !isError && page > pageCount && pageCount > 0) {
            setParam(ROAD_DOCUMENTS_PAGE, pageCount)
        }
    }, [isError, isLoading, page, pageCount, setParam])

    return (
        <Styled.Container className={className} isMobile={isMobile} id="documentRoadSidebarContent-container">
            <Styled.HeaderWrapper isMobile={isMobile}>
                <Styled.RoadDetailInfo road={road} />
            </Styled.HeaderWrapper>
            <Styled.List
                documents={data?.results}
                hoverHandling
                title={t(I18N_NAMESPACE, "containers.documentSidebar.title")}
                emptyText={t(I18N_NAMESPACE, "containers.documentList.emptyProjectText")}
                displayLoading={isLoading}
                subNetworks={selectedSubNetworkSlugs}
            />
            {pageCount > 1 && <Styled.PaginationContainer position={isMobile && currentBottomSheetHeight < 80 ? "static" : "sticky"} isMobile={isMobile}>
                <Pagination
                    page={page}
                    count={pageCount}
                    onChange={handleChangePage} />
            </Styled.PaginationContainer>}
        </Styled.Container>
    )
}

DocumentRoadSidebarContent.propTypes = {
    className: PropTypes.string,
}
